<template>
  <div style="width: 100%">
    <div v-if="verifying" class="text-center pa-8">
      <v-progress-circular indeterminate color="primary" />
      <div class="mt-3 grey--text">Verifying reset link…</div>
    </div>

    <CardLayoutAuthenticationForm
      v-else-if="tokenValid"
      title="Password Reset"
      submit-text="Reset"
      :in-process="inProcess"
      @cancel="cancelReset"
      @submit="tryReset"
    >
      <ValidatedTextField
        v-model="password"
        :disabled="inProcess"
        name="New Password"
        type="password"
        rules="required|max:128|passwordPolicy"
        vid="confirm"
        outlined
        dense
      />
      <div class="mb-4">
        <div
          v-for="req in passwordRequirements"
          :key="req.label"
          class="d-flex align-center caption mb-1"
          :class="req.met ? 'green--text' : 'grey--text'"
        >
          <v-icon
            x-small
            class="mr-2"
            :color="req.met ? 'green' : 'grey lighten-1'"
          >
            {{ req.met ? "mdi-check-circle" : "mdi-circle-outline" }}
          </v-icon>
          {{ req.label }}
        </div>
      </div>
      <ValidatedTextField
        v-model="password2"
        :disabled="inProcess"
        name="Confirm Password"
        type="password"
        rules="password:@confirm"
        outlined
        dense
      />
    </CardLayoutAuthenticationForm>
  </div>
</template>

<script>
import CardLayoutAuthenticationForm from "./CardLayoutAuthenticationForm.vue";
import ValidatedTextField from "../utils/ValidatedTextField.vue";

import { onBeforeMount, ref } from "vue";
import { required, max, min } from "vee-validate/dist/rules";
import { extend } from "vee-validate";

import useUser from "./useUser";
import useAlerts from "@tod-ui/composables/useAlerts";
import { redirectToLogin } from "../../router/auth-hooks";
import { isTokenExpired } from "../../apollo-client/apollo-token";
import { CHANGE_PASSWORD_TOKEN_VALID } from "./graphql";

extend("max", {
  ...max,
  message: "{_field_} may not be longer than {length} characters",
});

extend("min", {
  ...min,
  message: "{_field_} must be at least {length} characters",
});

extend("required", {
  ...required,
  message: "{_field_} can not be empty",
});

extend("password", {
  params: ["target"],
  validate(value, { target }) {
    return value === target;
  },
  message: "Password confirmation does not match",
});

// Matches the invite password-setup policy: ≥8 chars, an uppercase letter, a
// number, and a special character. The live checklist below the field guides
// the user; this rule blocks submit until every requirement is met.
extend("passwordPolicy", {
  validate(value) {
    return (
      typeof value === "string" &&
      value.length >= 8 &&
      /[A-Z]/.test(value) &&
      /[0-9]/.test(value) &&
      /[^A-Za-z0-9]/.test(value)
    );
  },
  message: "Password must meet all the requirements below",
});

export default {
  name: "PageChangePassword",
  components: {
    CardLayoutAuthenticationForm,
    ValidatedTextField,
  },
  props: {
    token: {
      type: String,
      default: null,
    },
  },
  setup() {
    const password = ref("");
    const password2 = ref("");
    const verifying = ref(true);
    const tokenValid = ref(false);
    const { inProcess, resetPassword } = useUser();
    const { addAlert, clearAllAlerts } = useAlerts();

    onBeforeMount(() => {
      clearAllAlerts();
    });

    return {
      password,
      password2,
      verifying,
      tokenValid,
      inProcess,
      resetPassword,
      addAlert,
    };
  },
  computed: {
    // Live checklist mirroring the invite password-setup page + passwordPolicy rule.
    passwordRequirements() {
      const p = this.password || "";
      return [
        { label: "At least 8 characters", met: p.length >= 8 },
        { label: "One uppercase letter", met: /[A-Z]/.test(p) },
        { label: "One number", met: /[0-9]/.test(p) },
        { label: "One special character (!@#$…)", met: /[^A-Za-z0-9]/.test(p) },
      ];
    },
  },
  mounted() {
    this.verifyToken();
  },
  methods: {
    // Validate the reset link on page load (without consuming it) so an
    // already-used or expired link is rejected up front and the user is sent to
    // login, instead of showing a form that only fails on submit.
    async verifyToken() {
      if (!this.token || isTokenExpired(this.token)) {
        return this.rejectLink();
      }
      try {
        const { data } = await this.$apollo.query({
          query: CHANGE_PASSWORD_TOKEN_VALID,
          variables: { token: this.token },
          fetchPolicy: "network-only",
        });
        if (data?.changePasswordTokenValid) {
          this.tokenValid = true;
        } else {
          return this.rejectLink();
        }
      } catch {
        return this.rejectLink();
      } finally {
        this.verifying = false;
      }
    },
    rejectLink() {
      this.verifying = false;
      this.addAlert({
        type: "error",
        message:
          "This password reset link is no longer valid — it may have already been used or expired. Please request a new password reset.",
        timeout: 15,
      });
      redirectToLogin();
    },
    async tryReset() {
      const result = await this.resetPassword(this.$apollo, this.password);
      if (result) {
        this.addAlert({
          type: "success",
          message: "Password has been changed successfully. You can login now",
          timeout: 15,
        });
      } else {
        this.addAlert({
          type: "error",
          message:
            "This password reset link is no longer valid — it may have already been used or expired. Please request a new password reset.",
          timeout: 15,
        });
      }
      redirectToLogin();
    },
    cancelReset: redirectToLogin,
  },
};
</script>
